import { forwardRef } from "react"
import { Light as SyntaxHighlighter } from "react-syntax-highlighter"
import bash from "react-syntax-highlighter/dist/esm/languages/hljs/bash"
import erb from "react-syntax-highlighter/dist/esm/languages/hljs/erb"
import go from "react-syntax-highlighter/dist/esm/languages/hljs/go"
import javascript from "react-syntax-highlighter/dist/esm/languages/hljs/javascript"
import json from "react-syntax-highlighter/dist/esm/languages/hljs/json"
import kotlin from "react-syntax-highlighter/dist/esm/languages/hljs/kotlin"
import python from "react-syntax-highlighter/dist/esm/languages/hljs/python"
import ruby from "react-syntax-highlighter/dist/esm/languages/hljs/ruby"
import swift from "react-syntax-highlighter/dist/esm/languages/hljs/swift"
import typescript from "react-syntax-highlighter/dist/esm/languages/hljs/typescript"
import xml from "react-syntax-highlighter/dist/esm/languages/hljs/xml"
import { atomOneDark } from "react-syntax-highlighter/dist/esm/styles/hljs"

export const DEFAULT_CODE_LANGUAGE = "ruby"

export const SUPPORTED_CODE_LANGUAGES = [
  ["ruby", ruby],
  ["erb", erb],
  ["javascript", javascript],
  ["typescript", typescript],
  ["python", python],
  ["go", go],
  ["json", json],
  ["xml", xml],
  ["swift", swift],
  ["kotlin", kotlin],
  ["bash", bash],
] as const

for (const [name, definition] of SUPPORTED_CODE_LANGUAGES) {
  SyntaxHighlighter.registerLanguage(name, definition)
}

function normalizedLanguage(language?: string | null) {
  const value = language?.trim().toLowerCase()
  return SUPPORTED_CODE_LANGUAGES.some(([name]) => name === value)
    ? value
    : "plaintext"
}

interface Props {
  codeSnippet: string
  codeLanguage: string
  compact?: boolean
}

const CodeTakeCard = forwardRef<HTMLDivElement, Props>(function CodeTakeCard(
  { codeSnippet, codeLanguage, compact = false },
  ref,
) {
  return (
    <div
      ref={ref}
      className="inline-flex max-w-full self-start bg-[linear-gradient(135deg,#f97316_0%,#fb7185_38%,#a855f7_68%,#2563eb_100%)] p-5 shadow-[0_24px_80px_rgba(15,23,42,0.28)]"
      data-testid="code-take-card-backdrop"
      style={{ maxWidth: "600px" }}
    >
      <div className="flex min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-800/80 bg-slate-950 text-slate-100 shadow-md">
        <div
          className="flex items-center gap-2 border-b border-slate-800 bg-slate-900/95 px-4 py-3"
          data-testid="code-take-card-chrome"
        >
          <span
            aria-hidden="true"
            className="size-3 rounded-full bg-[#ff5f57]"
            data-testid="code-take-card-control-close"
          />
          <span
            aria-hidden="true"
            className="size-3 rounded-full bg-[#febc2e]"
            data-testid="code-take-card-control-minimize"
          />
          <span
            aria-hidden="true"
            className="size-3 rounded-full bg-[#28c840]"
            data-testid="code-take-card-control-fullscreen"
          />
        </div>
        <SyntaxHighlighter
          language={normalizedLanguage(codeLanguage)}
          style={atomOneDark}
          wrapLongLines
          customStyle={{
            margin: 0,
            borderRadius: 0,
            background: "transparent",
            padding: compact ? "18px" : "28px",
            fontSize: compact ? "12px" : "14px",
            lineHeight: compact ? 1.5 : 1.6,
            overflowX: "hidden",
            width: "fit-content",
            maxWidth: "100%",
          }}
          codeTagProps={{
            style: {
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            },
          }}
        >
          {codeSnippet.trim().length > 0
            ? codeSnippet
            : "// Add your code snippet"}
        </SyntaxHighlighter>
      </div>
    </div>
  )
})

export default CodeTakeCard
